import { useState } from "react"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect, type LinkProps } from "@tanstack/react-router"
import { Download, Search } from "lucide-react"
import { toast } from "sonner"
import { AuditCategory, type AuditEvent } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DataTable, Heading, Pick } from "@/components/workspace/primitives"
import { formatDateTime } from "@/lib/format"
import { auditQuery, teamQuery, workspaceQuery, type AuditFilters } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/audit")({
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("manage_workspace")) throw redirect({ to: "/w/$workspace/settings/general", params })
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(teamQuery(params.workspace)),
  component: AuditLog,
})

const DATE_RANGES = [
  { value: "7", label: "Last 7 days" },
  { value: "today", label: "Today" },
  { value: "30", label: "Last 30 days" },
  { value: "all", label: "All time" },
]
const CATEGORY_LABELS: Record<AuditCategory, string> = {
  workspace: "Workspace",
  team: "Team",
  channels: "Channels",
  automations: "Automations",
  campaigns: "Campaigns",
  contacts: "Contacts",
  moderation: "Moderation",
  consent: "Consent",
}
const RESULT_LABELS = { success: "Success", recorded: "Recorded", failed: "Failed" } as const

function fromDate(range: string): string | undefined {
  if (range === "all") return undefined
  const date = new Date()
  if (range === "today") date.setHours(0, 0, 0, 0)
  else date.setDate(date.getDate() - Number(range))
  return date.toISOString()
}

function resourceLink(event: AuditEvent): LinkProps["to"] | null {
  switch (event.resourceType) {
    case "workspace":
      return "/w/$workspace/settings/general"
    case "membership":
    case "invitation":
      return "/w/$workspace/settings/team"
    default:
      return null
  }
}

/** Screen 47. */
function AuditLog() {
  const { workspace } = useWorkspaceContext()
  const team = useSuspenseQuery(teamQuery(workspace.slug)).data
  const [range, setRange] = useState("7")
  const [actor, setActor] = useState("all")
  const [category, setCategory] = useState("all")
  const [query, setQuery] = useState("")
  const filters: AuditFilters = {
    q: query.trim() || undefined,
    actorUserId: actor === "all" ? undefined : actor,
    category: category === "all" ? undefined : category,
    from: fromDate(range),
  }
  const audit = useQuery({ ...auditQuery(workspace.slug, filters), placeholderData: (previous) => previous })
  const events = audit.data?.events ?? []

  function exportLog() {
    const lines = ["Time,Actor,Event,Resource,Category,Result", ...events.map((e) => [e.createdAt, e.actorName, e.summary, e.resourceLabel ?? "", e.category, e.result].map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(","))]
    const blob = new Blob([lines.join("\n")], { type: "text/csv" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `${workspace.slug}-audit-log.csv`
    a.click()
    URL.revokeObjectURL(a.href)
    toast("Audit log exported")
  }

  return (
    <>
      <Heading
        title="Audit log"
        description="A clear record of who changed what, and when."
        action={
          <Button variant="outline" onClick={exportLog} disabled={!events.length}>
            <Download />
            Export
          </Button>
        }
      />
      <div className="ws-audit-filters">
        <Pick label="Date range" value={range} options={DATE_RANGES} onChange={setRange} />
        <Pick label="Actor" value={actor} options={[{ value: "all", label: "All actors" }, ...team.members.map((m) => ({ value: m.userId, label: m.name }))]} onChange={setActor} />
        <Pick label="Category" value={category} options={[{ value: "all", label: "All events" }, ...AuditCategory.options.map((value) => ({ value, label: CATEGORY_LABELS[value] }))]} onChange={setCategory} />
      </div>
      <div className="ws-toolbar">
        <div className="ws-search">
          <Search size={15} />
          <Input aria-label="Search audit log" placeholder="Search events…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <span className="ws-hint">
          {events.length} event{events.length === 1 ? "" : "s"}
          {audit.data?.nextCursor ? " shown" : ""}
        </span>
      </div>
      <DataTable
        heads={["Time", "Actor", "Event", "Resource", "Result"]}
        rowKeys={events.map((e) => e.id)}
        rows={events.map((event) => {
          const to = resourceLink(event)
          return [
            <span className="whitespace-nowrap text-muted-foreground">{formatDateTime(event.createdAt)}</span>,
            event.actorName,
            event.summary,
            to && event.resourceLabel ? (
              <Link className="ws-table-link" to={to} params={{ workspace: workspace.slug }}>
                {event.resourceLabel}
              </Link>
            ) : (
              <span className="text-muted-foreground">{event.resourceLabel ?? "—"}</span>
            ),
            <Badge variant={event.result === "failed" ? "destructive" : "secondary"}>{RESULT_LABELS[event.result]}</Badge>,
          ]
        })}
      />
      {audit.isSuccess && !events.length && <p className="ws-empty">No events match these filters.</p>}
      {audit.isError && <p className="ws-empty">The audit log could not be loaded. Try again.</p>}
      <p className="ws-hint mt-5">{audit.data?.nextCursor ? "Showing the most recent 100 events. Narrow the filters or export for the full record." : `Showing all matching events from ${workspace.name}.`}</p>
    </>
  )
}
